import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { PermitPublicStatus, PermitStep, PermitType, PersonRole } from "@ha/data/basic";
import { HaModalService } from "@ha/ui/common";
import { TranslocoService } from "@ngneat/transloco";

import { PermitListItem } from "../../models/permit-list-item.model";
import {
    RemovePlannedPermitModalComponent,
    RemovePlannedPermitModalComponentData,
} from "../remove-planned-permit-modal/remove-planned-permit-modal.component";

@Component({
    selector: "pmt-permit-list-item",
    templateUrl: "./permit-list-item.component.html",
    styleUrls: ["./permit-list-item.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PermitListItemComponent {
    @Input() public item!: PermitListItem;

    public PermitPublicStatus = PermitPublicStatus;
    public PermitType = PermitType;
    public PermitStep = PermitStep;
    public PersonRole = PersonRole;

    constructor(private haModalService: HaModalService, private translocoService: TranslocoService) { }

    public get hasFlammableAtexContext(): boolean {
        return this.item.type === PermitType.flammableAtex || this.item.hasFlammableAtexPermit;
    }

    public get isSuperintendent(): boolean {
        return this.item.roles?.some(_ => _ == PersonRole.Superintendent) === true;
    }

    public get showFlammableAtexBadge(): boolean {
        return this.hasFlammableAtexContext && this.isSuperintendent;
    }

    public get showFlammableAtexExplosionIcon(): boolean {
        return this.hasFlammableAtexContext;
    }

    public get showFlammableAtexWindWarningIcon(): boolean {
        return this.hasFlammableAtexContext && this.item.flammableAtexHasMeasurements;
    }

    public get isStandaloneFlammableAtex(): boolean {
        return this.item.type === PermitType.flammableAtex && !this.item.hasPermitRelation;
    }

    public get isPlannedFlammableAtex(): boolean {
        return this.item.type === PermitType.flammableAtex && this.item.publicStatus === PermitPublicStatus.planned;
    }

    public get showFlammableIcon(): boolean {
        return !this.isStandaloneFlammableAtex;
    }

    public get roles() {
        return this.item.roles.map(_ => this.translocoService.translate("permit.permitRole." + _.toString())).join(", ");
    }

    public get canRemovePlannedPermit(): boolean {
        return this.item.roles.some(_ => _ == PersonRole.PermitIssuer);
    }

    public get routerLink() {
        if (!(this.item.roles?.length > 0 || this.item.isIssuer)) {
            return undefined;
        }

        if (this.item.type === PermitType.flammableAtex && this.item.step === PermitStep.started ) {
            return undefined;
        }

        return [this.item.id];
    }

    public get isNotFlammableAndCompleted() {
        return !this.item.isFlammableHotWork && this.item.publicStatus === PermitPublicStatus.completed;
    }

    public get isDemoAndCompleted() {
        return this.item.isDemo && this.item.publicStatus === PermitPublicStatus.completed;
    }

    public remove(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this.haModalService.open(RemovePlannedPermitModalComponent, true, <RemovePlannedPermitModalComponentData>{
            permitId: this.item.id,
            workplace: this.fullPermitName(this.item),
            isFlammableAtexPermit: this.item.type === PermitType.flammableAtex
        });
    }

    public fullPermitName(item: PermitListItem): string {
        if (item.workplaceContainer == item.workplace) {
            return item.workplace;
        } else {
            return item.workplace?.length > 0 ? item.workplaceContainer + " - " + item.workplace : item.workplaceContainer;
        }
    }

    public getInitials(name: string): string {
        return name.split(" ").map((n)=>n[0]).join(".").toUpperCase();
    }

    public getProgress(start: string | null, end: string | null): number {
        if (!start || !end) {
            return 0;
        }

        const startDate = new Date(start).getTime();
        const endDate = new Date(end).getTime();
        const now = new Date().getTime();

        if (isNaN(startDate) || isNaN(endDate) || endDate <= startDate) {
            return 0;
        }

        if (now > endDate)
            return 100;

        const totalHours = (endDate - startDate) / (3600 * 1000);
        const passedHours = (now - startDate) / (3600 * 1000);
        return Math.ceil(passedHours/totalHours * 100);
    }
}
